import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';
import { FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InvoicesStatusChartProps {
  data?: Array<{
    status: string;
    count: number;
    total: number;
  }>;
  loading: boolean;
  className?: string;
}

export default function InvoicesStatusChart({ data, loading, className }: InvoicesStatusChartProps) {
  if (loading) {
    return (
      <Card className={cn("athlyn-card", className)}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Invoices by Status</span>
            <FileText className="w-5 h-5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  const chartData = data || [];
  
  // Format and enhance the data
  const formattedData = chartData.map(item => ({
    status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
    count: item.count,
    amount: item.total / 1000, // Convert to thousands
    color: getStatusColor(item.status)
  }));

  function getStatusColor(status: string) {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'hsl(142, 71%, 45%)'; // Green
      case 'pending':
        return 'hsl(47, 96%, 53%)'; // Yellow
      case 'overdue':
        return 'hsl(0, 84%, 60%)'; // Red
      default:
        return 'hsl(var(--muted-foreground))';
    }
  }

  return (
    <Card className={cn("athlyn-card", className)}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Invoices by Status</span>
          <FileText className="w-5 h-5 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {formattedData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No invoice data available
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={formattedData} 
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                className="stroke-muted" 
              />
              <XAxis 
                dataKey="status" 
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis 
                yAxisId="left"
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                label={{ 
                  value: 'Count', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: 'hsl(var(--muted-foreground))' }
                }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                className="text-xs"
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => `${value}k`}
                label={{ 
                  value: 'Amount (AED thousands)', 
                  angle: 90, 
                  position: 'insideRight',
                  style: { fill: 'hsl(var(--muted-foreground))' }
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
                formatter={(value: number, name: string) => {
                  if (name === 'Count') return [value, name];
                  return [`AED ${value.toFixed(1)}k`, 'Amount'];
                }}
              />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '20px' 
                }}
              />
              <Bar 
                yAxisId="left"
                dataKey="count" 
                fill="hsl(var(--primary))"
                name="Count"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                yAxisId="right"
                dataKey="amount" 
                fill="hsl(var(--muted))"
                name="Amount (AED thousands)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}